import { useRef, useEffect, useState } from 'react'
import { LineChart, Line, BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../integrations/supabase/client'
import {
  ArrowRight, BarChart2, FileText, Building2, Heart,
  Newspaper, Bed, Sun, Home, Activity, Users, MapPin,
  Shield, Layers, Radio, Vote, CheckCircle, XCircle
} from 'lucide-react'
import { REFERENDUM_COMUNI } from '../data/referendum'
import DistrictMap from './DistrictMap'

/* ── Mini preview components ──────────────────────────────────────────── */

function MiniKpi({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-2, rgba(0,0,0,0.45))', fontFamily: 'var(--font-mono)' }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 700, color, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>{value}</span>
    </div>
  )
}

function MiniBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', color: 'rgba(255,255,255,0.55)' }}>{pct}%</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: color, opacity: 0.85 }} />
      </div>
    </div>
  )
}

function PreviewDemografici() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px' }}>
      
      <div style={{ marginTop: 4 }}>
        
        <ResponsiveContainer width="100%" height={80}>
          <LineChart data={[
            { anno: '2019', valore: 51.2 },
            { anno: '2020', valore: 52.8 },
            { anno: '2021', valore: 53.1 },
            { anno: '2022', valore: 54.4 },
            { anno: '2023', valore: 55.7 },
            { anno: '2024', valore: 56.2 },
          ]} margin={{ top: 2, right: 6, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="rgba(0,0,0,0.07)" strokeDasharray="3 3" />
            <XAxis dataKey="anno" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} domain={[49, 58]} />
            <Tooltip
              contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11 }}
              formatter={(v: number | undefined) => [Number(v ?? 0).toFixed(1), 'Indice']}
            />
            <Line type="monotone" dataKey="valore" stroke="#dc2626" strokeWidth={2} dot={{ r: 2.5, fill: '#dc2626' }} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function PreviewPdz() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px' }}>
      <div style={{ display: 'flex', gap: 14 }}>
        <MiniKpi label="Obiettivi" value="124" color="#10b981" />
        <MiniKpi label="Azioni" value="312" color="#06b6d4" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4 }}>
        <MiniBar label="Inclusione" pct={68} color="#10b981" />
        <MiniBar label="Benessere" pct={54} color="#8b5cf6" />
        <MiniBar label="Disabilità" pct={42} color="#f59e0b" />
        
      </div>
    </div>
  )
}

const CDC_BAR_DATA = [
  { name: 'Attive', value: 14, color: '#10b981' },
  { name: 'Non attive', value: 3, color: '#ef4444' },
]

function PreviewCdc() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px' }}>
      
      <ResponsiveContainer width="100%" height={96}>
        <BarChart data={CDC_BAR_DATA} margin={{ top: 8, right: 16, left: -24, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid stroke="rgba(0,0,0,0.07)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} domain={[0, 18]} />
          <Tooltip
            contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11 }}
            formatter={(v: number | undefined) => [Number(v ?? 0), 'Case']}
          />
          <Bar dataKey="value" radius={[5, 5, 0, 0]} maxBarSize={52}>
            <LabelList dataKey="value" position="top" style={{ fontSize: 11, fontWeight: 700, fill: '#334155' }} />
            {CDC_BAR_DATA.map(entry => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function PreviewStrutture() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '12px 14px' }}>
      <MiniKpi label="Strutture" value="98" color="#0ea5e9" />
      <MiniKpi label="Ospedali" value="5" color="#ef4444" />
      <MiniKpi label="Neuropsichiatria" value="1" color="#8b5cf6" />
      <MiniKpi label="Ambulatori" value="8" color="#10b981" />
    </div>
  )
}

function PreviewRsa() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px' }}>
      <div style={{ display: 'flex', gap: 14 }}>
        <MiniKpi label="RSA" value="48" color="#6366f1" />
        <MiniKpi label="Posti letto" value="4.2k" color="#f59e0b" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 11, color: 'var(--text-2, rgba(0,0,0,0.45))', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Posti occupati</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#6366f1', fontFamily: 'var(--font-mono)' }}>95%</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: 'rgba(99,102,241,0.15)', overflow: 'hidden' }}>
          <div style={{ width: '95%', height: '100%', borderRadius: 4, background: '#6366f1' }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-2, rgba(0,0,0,0.45))', fontFamily: 'var(--font-mono)', marginTop: 2 }}>30 posti liberi</span>
      </div>
    </div>
  )
}

function PreviewNews() {
  const items = ['Nuovo bando per servizi sociali MB', 'Aggiornamento liste attesa RSA', 'Nuovi Ambulatori']
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, padding: '12px 14px' }}>
      {items.map((item, i) => (
        <div key={i} style={{
          padding: '7px 0',
          borderBottom: i < items.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <div style={{ width: 3, height: 3, borderRadius: '50%', background: '#06b6d4', marginTop: 5, flexShrink: 0 }} />
          <span style={{ fontSize: 10, color: 'var(--text-1)', lineHeight: 1.45 }}>{item}</span>
        </div>
      ))}
    </div>
  )
}

/* ── Radar section ───────────────────────────────────────────────────── */

const RADAR_PERIOD = 7 // seconds per revolution

// Approximate positions within a 380×380 container that shows the 700×500 SVG map.
// SVG is width-limited: scale = 380/700 = 0.543, rendered height = 500*0.543 = 271
// Top offset = (380 - 271) / 2 = 54.5px → 14.3%
// x% = svgX / 700 * 100
// y% = (54.5 + svgY * 0.543) / 380 * 100
const RADAR_DOTS: { x: number; y: number; label: string; color: string; kind: string }[] = [
  { x: 57,  y: 62,  label: 'Osp. San Gerardo',    color: '#ef4444', kind: 'Ospedale' },
  { x: 30,  y: 54,  label: 'Osp. Desio',           color: '#ef4444', kind: 'Ospedale' },
  { x: 72,  y: 50,  label: 'Osp. Vimercate',       color: '#ef4444', kind: 'Ospedale' },
  { x: 46,  y: 30,  label: 'CdC Carate B.',         color: '#e11d48', kind: 'Casa CdC' },
  { x: 27,  y: 41,  label: 'CdC Seregno',           color: '#e11d48', kind: 'Casa CdC' },
  { x: 64,  y: 69,  label: 'CdC Brugherio',         color: '#e11d48', kind: 'Casa CdC' },
  { x: 69,  y: 37,  label: 'CdC Concorezzo',        color: '#e11d48', kind: 'Casa CdC' },
  { x: 44,  y: 48,  label: 'RSA Lissone',           color: '#6366f1', kind: 'RSA' },
  { x: 76,  y: 57,  label: 'RSA Agrate B.',          color: '#6366f1', kind: 'RSA' },
  { x: 21,  y: 58,  label: 'RSA Cesano M.',          color: '#6366f1', kind: 'RSA' },
  { x: 53,  y: 74,  label: 'CDI Sesto S.G.',        color: '#10b981', kind: 'CDI' },
  { x: 38,  y: 37,  label: 'CPS Giussano',          color: '#8b5cf6', kind: 'CPS' },
]

function getDotAngle(x: number, y: number): number {
  const dx = x - 50
  const dy = y - 50
  const rad = Math.atan2(dx, -dy) // clockwise from top
  return (((rad * 180) / Math.PI) % 360 + 360) % 360
}

const RADAR_KEYFRAMES = `
@keyframes radar-sweep {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes radar-line {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes radar-dot-glow {
  0%,100% { transform: translate(-50%,-50%) scale(1);   opacity: 0.45; }
  3%      { transform: translate(-50%,-50%) scale(2.4); opacity: 1; }
  10%     { transform: translate(-50%,-50%) scale(1.3); opacity: 0.8; }
  18%     { transform: translate(-50%,-50%) scale(1);   opacity: 0.45; }
}
@keyframes radar-ring-expand {
  0%,100% { transform: translate(-50%,-50%) scale(1);   opacity: 0; }
  2%      { opacity: 0.8; }
  18%     { transform: translate(-50%,-50%) scale(3.8); opacity: 0; }
}
@keyframes radar-label-flash {
  0%,100% { opacity: 0; }
  3%      { opacity: 1; }
  20%     { opacity: 0; }
}
@keyframes radar-blink {
  0%,49%,100% { opacity: 1; }
  50%,99%     { opacity: 0.4; }
}
@keyframes ticker-exit {
  from { transform: translateY(0);     opacity: 1; }
  to   { transform: translateY(-115%); opacity: 0; }
}
@keyframes ticker-enter {
  from { transform: translateY(115%);  opacity: 0; }
  to   { transform: translateY(0);     opacity: 1; }
}
`

/* ── Hero ticker ─────────────────────────────────────────────────────── */

const TICKER_SECTIONS = [
  { path: '/strutturesanitarie',  label: 'Strutture Sanitarie' },
  { path: '/casedellacomunita',   label: 'Case della Comunità' },
  { path: '/rsa',                 label: 'Residenze Sanitarie' },
  { path: '/cdi',                 label: 'Centri Diurni Integrati' },
  { path: '/pianidizona',         label: 'Piani di Zona' },
  { path: '/rsd',                 label: 'Residenze Disabili' },
  { path: '/css',                 label: 'Comunità Socio Sanitarie' },
  { path: '/cdd',                 label: 'Centri Diurni Disabili' },
  { path: '/news',                label: 'Notizie' },
  { path: '/poloterritoriale',    label: 'Polo Territoriale' },
  { path: '/datidemografici',     label: 'Dati Demografici' },
]

function HeroTicker() {
  const [idx, setIdx] = useState(0)
  const [prevIdx, setPrevIdx] = useState<number | null>(null)

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i: number) => {
        const next = (i + 1) % TICKER_SECTIONS.length
        setPrevIdx(i)
        return next
      })
    }, 2400)
    return () => clearInterval(t)
  }, [])

  const current = TICKER_SECTIONS[idx]
  const prev = prevIdx !== null ? TICKER_SECTIONS[prevIdx] : null

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
      color: 'var(--text-0)', borderRadius: 10, padding: '12px 24px',
      fontSize: 14, fontWeight: 600,
      minWidth: 270, overflow: 'hidden',
      userSelect: 'none', pointerEvents: 'none',
    }}>
      <MapPin size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
      <div style={{ position: 'relative', height: '1.25em', flex: 1, overflow: 'hidden' }}>
        {prev && (
          <span
            key={`exit-${prevIdx}`}
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center',
              whiteSpace: 'nowrap',
              animation: 'ticker-exit 0.38s cubic-bezier(0.4,0,0.2,1) forwards',
            }}
          >
            {prev.label}
          </span>
        )}
        <span
          key={`enter-${idx}`}
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center',
            whiteSpace: 'nowrap',
            animation: prevIdx !== null
              ? 'ticker-enter 0.38s cubic-bezier(0.4,0,0.2,1) forwards'
              : 'none',
          }}
        >
          {current.label}
        </span>
      </div>
    </div>
  )
}

const ROTATING_STATS: { value: string; label: string }[][] = [
  [
    { value: '183+',  label: 'Strutture totali'      },
    { value: '42',    label: 'RSA attive'             },
    { value: '17',    label: 'Case della Comunità'    },
    { value: '23',    label: 'Centri Diurni Disabili' },
    { value: '24',    label: 'Centri diurni integrati'},
  ],
  [
    { value: '55',    label: 'Comuni'                 },
    { value: '22',    label: 'Comuni – Vimercate'     },
    { value: '13',    label: 'Comuni – Carate'        },
    { value: '10',    label: 'Comuni – Seregno'       },
    { value: '7',     label: 'Comuni – Desio'         },
  ],
  [
    { value: '54',     label: 'Obiettivi Sociali'    },
    { value: '81',     label: 'Obiettivi Sanitari'   },
    { value: '5',    label: 'Ospedali'               },
    { value: '8',    label: 'Ambulatori'             },
    { value: '870+km²', label: 'Superficie Provincia' },
  ],
  [
    { value: '870k+', label: 'Residenti'              },
    { value: '194k+', label: 'Residenti – Vimercate'  },
    { value: '172k+', label: 'Residenti – Monza'      },
    { value: '170k+', label: 'Residenti – Seregno'    },
    { value: '154k+', label: 'Residenti – Carate'     },
  ],
]

function RotatingStat({ items, initialDelay }: { items: { value: string; label: string }[]; initialDelay: number }) {
  const [idx, setIdx] = useState(0)
  const [prevIdx, setPrevIdx] = useState<number | null>(null)

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    const schedule = () => {
      const delay = 2200 + Math.random() * 3800
      timeout = setTimeout(() => {
        setIdx(i => {
          const next = (i + 1) % items.length
          setPrevIdx(i)
          return next
        })
        schedule()
      }, delay)
    }
    timeout = setTimeout(schedule, initialDelay)
    return () => clearTimeout(timeout)
  }, [items.length, initialDelay])

  const current = items[idx]
  const prev = prevIdx !== null ? items[prevIdx] : null

  return (
    <div style={{ textAlign: 'center', minWidth: 120 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 700, color: 'var(--text-0)', letterSpacing: '-0.02em', position: 'relative', height: '1.3em', overflow: 'hidden' }}>
        {prev && (
          <span key={`v-exit-${prevIdx}`} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', animation: 'ticker-exit 0.38s cubic-bezier(0.4,0,0.2,1) forwards' }}>
            {prev.value}
          </span>
        )}
        <span key={`v-enter-${idx}`} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', animation: prevIdx !== null ? 'ticker-enter 0.38s cubic-bezier(0.4,0,0.2,1) forwards' : 'none' }}>
          {current.value}
        </span>
      </div>
      <div style={{ position: 'relative', height: '2.8em', overflow: 'hidden', marginTop: 2 }}>
        {prev && (
          <span key={`l-exit-${prevIdx}`} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'normal', lineHeight: 1.4, animation: 'ticker-exit 0.38s cubic-bezier(0.4,0,0.2,1) forwards' }}>
            {prev.label}
          </span>
        )}
        <span key={`l-enter-${idx}`} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'normal', lineHeight: 1.4, animation: prevIdx !== null ? 'ticker-enter 0.38s cubic-bezier(0.4,0,0.2,1) forwards' : 'none' }}>
          {current.label}
        </span>
      </div>
    </div>
  )
}

function RadarSection() {
  const styleRef = useRef<HTMLStyleElement | null>(null)
  useEffect(() => {
    if (styleRef.current) return
    const el = document.createElement('style')
    el.textContent = RADAR_KEYFRAMES
    document.head.appendChild(el)
    styleRef.current = el
    return () => { el.remove(); styleRef.current = null }
  }, [])

  const SIZE = 380

  const MONITORING_STATS = [
    { label: 'Strutture monitorate', value: '183', unit: 'presidi', color: '#06b6d4' },
    { label: 'Posti disponibili', value: '1.240', unit: 'Monitoraggio', color: '#10b981' },
    { label: 'In lista d\'attesa', value: '3.870', unit: 'utenti', color: '#f59e0b' },
    { label: 'RSA attive', value: '48', unit: 'strutture', color: '#6366f1' },
  ]

  return (
    <div style={{ background: 'var(--bg-1)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '72px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 56, alignItems: 'center' }}>

        {/* ── Left: radar map ──────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>

          {/* Outer glow ring */}
          <div style={{ position: 'absolute', width: SIZE + 40, height: SIZE + 40, borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(6,182,212,0.08) 0%, transparent 70%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />

          <div style={{
            position: 'relative',
            width: SIZE,
            height: SIZE,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '1.5px solid rgba(6,182,212,0.35)',
            boxShadow: '0 0 0 1px rgba(6,182,212,0.2), 0 0 40px rgba(6,182,212,0.12)',
            flexShrink: 0,
          }}>
            {/* Map base */}
            <div style={{ position: 'absolute', inset: 0, filter: 'saturate(0.9) brightness(1)', display: 'flex', alignItems: 'center', pointerEvents: 'none' }}>
              <div style={{ width: '100%', transform: 'scale(1.22)', transformOrigin: 'center center' }}>
                <DistrictMap
                  onSelectDistrict={() => {}}
                  onClearSelection={() => {}}
                  selectedDistrictId={null}
                  showLegend={false}
                  showInstructionText={false}
                  allowDistrictSelection={false}
                />
              </div>
            </div>

            {/* Subtle vignette to blend edges into circle */}
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle, transparent 60%, rgba(0,0,0,0.08) 100%)', borderRadius: '50%', pointerEvents: 'none' }} />

            {/* Concentric circles */}
            {[25, 50, 75, 100].map(pct => (
              <div key={pct} style={{
                position: 'absolute',
                width: `${pct}%`, height: `${pct}%`,
                border: '1px solid rgba(6,182,212,0.3)',
                borderRadius: '50%',
                top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                pointerEvents: 'none',
              }} />
            ))}

            {/* Cross lines */}
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: 'rgba(6,182,212,0.25)', transform: 'translateY(-0.5px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'rgba(6,182,212,0.25)', transform: 'translateX(-0.5px)', pointerEvents: 'none' }} />

            {/* Radar sweep gradient */}
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: 'conic-gradient(from 0deg, transparent 0deg, transparent 310deg, rgba(6,182,212,0.03) 320deg, rgba(6,182,212,0.12) 338deg, rgba(6,182,212,0.28) 350deg, rgba(6,182,212,0.45) 360deg)',
              animation: `radar-sweep ${RADAR_PERIOD}s linear infinite`,
              pointerEvents: 'none',
            }} />

            {/* Radar scan line */}
            <div style={{
              position: 'absolute',
              top: '50%', left: '50%',
              width: '50%', height: 1,
              transformOrigin: 'left center',
              background: 'linear-gradient(to right, rgba(6,182,212,0.8), rgba(6,182,212,0.1))',
              boxShadow: '0 0 4px rgba(6,182,212,0.6)',
              animation: `radar-line ${RADAR_PERIOD}s linear infinite`,
              pointerEvents: 'none',
            }} />

            {/* Center dot */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              width: 7, height: 7, borderRadius: '50%',
              background: '#06b6d4',
              boxShadow: '0 0 8px #06b6d4, 0 0 16px rgba(6,182,212,0.5)',
              pointerEvents: 'none',
            }} />

            {/* Structure dots */}
            {RADAR_DOTS.map((dot, i) => {
              const angle = getDotAngle(dot.x, dot.y)
              const delay = `${(angle / 360) * RADAR_PERIOD}s`
              return (
                <div key={i}>
                  {/* Dot */}
                  <div style={{
                    position: 'absolute',
                    left: `${dot.x}%`, top: `${dot.y}%`,
                    width: 7, height: 7,
                    borderRadius: '50%',
                    background: dot.color,
                    boxShadow: `0 0 4px ${dot.color}`,
                    animation: `radar-dot-glow ${RADAR_PERIOD}s linear infinite`,
                    animationDelay: delay,
                    pointerEvents: 'none',
                  }} />
                  {/* Expanding ring */}
                  <div style={{
                    position: 'absolute',
                    left: `${dot.x}%`, top: `${dot.y}%`,
                    width: 7, height: 7,
                    borderRadius: '50%',
                    border: `1px solid ${dot.color}`,
                    animation: `radar-ring-expand ${RADAR_PERIOD}s linear infinite`,
                    animationDelay: delay,
                    pointerEvents: 'none',
                  }} />
                  {/* Label */}
                  <div style={{
                    position: 'absolute',
                    left: `${dot.x + (dot.x > 50 ? -2 : 3)}%`,
                    top: `${dot.y - 5}%`,
                    transform: dot.x > 50 ? 'translateX(-100%)' : 'translateX(8px)',
                    fontSize: 8,
                    fontFamily: 'var(--font-mono)',
                    color: dot.color,
                    whiteSpace: 'nowrap',
                    animation: `radar-label-flash ${RADAR_PERIOD}s linear infinite`,
                    animationDelay: delay,
                    pointerEvents: 'none',
                    textShadow: `0 0 6px ${dot.color}`,
                  }}>
                    {dot.label}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Corner badge */}
          <div style={{
            position: 'absolute', bottom: -6, right: 'calc(50% - 190px - 4px)',
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)',
            borderRadius: 6, padding: '4px 10px',
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#06b6d4', animation: 'radar-blink 2s step-end infinite' }} />
            <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Live</span>
          </div>
        </div>

        {/* ── Right: info ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: 'var(--font-mono)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
            <Radio size={12} />
            Monitoraggio continuo
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, color: 'var(--text-0)', letterSpacing: '-0.02em', lineHeight: 1.15, marginBottom: 16 }}>
            Ogni struttura,<br />sempre aggiornata
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.75, marginBottom: 32 }}>
            Brianza Social Debug monitora in modo continuativo tutte le strutture socio-sanitarie
            della provincia. Per ogni presidio trovi in tempo reale
            i <strong style={{ color: 'var(--text-1)' }}>posti disponibili</strong>, e le <strong style={{ color: 'var(--text-1)' }}>liste d'attesa</strong> — così da avere sempre
            una visione chiara dello stato dei servizi sul territorio.
          </p>

          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {MONITORING_STATS.map(stat => (
              <div key={stat.label} style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border)',
                borderLeft: `2px solid ${stat.color}`,
                borderRadius: 10,
                padding: '14px 16px',
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color: stat.color, lineHeight: 1, letterSpacing: '-0.02em', marginBottom: 6 }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 600 }}>{stat.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'var(--font-mono)' }}>{stat.unit}</div>
              </div>
            ))}
          </div>

          <Link to="/datidemografici" style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            color: '#06b6d4', fontSize: 13, fontWeight: 600,
            textDecoration: 'none', marginTop: 24,
            opacity: 0.85,
          }}>
            Inizia
            <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </div>
  )
}

/* ── Screenshots section ─────────────────────────────────────────────── */

const SCREENSHOTS = [
  {
    title: 'RSA',
    subtitle: 'Scheda struttura',
    color: '#6366f1',
    url: 'brianza-social-debug.it',
    img: '/screenshots/1.png',
  },
  {
    title: 'Polo Territoriale',
    subtitle: 'Dettaglio servizio',
    color: '#10b981',
    url: 'brianza-social-debug.it',
    img: '/screenshots/2.png',
  },
  {
    title: 'Dati Demografici',
    subtitle: 'Dashboard provincia',
    color: '#06b6d4',
    url: 'brianza-social-debug.it',
    img: '/screenshots/3.png',
  },
]

function ReferendumSpotlight() {
  const totSi = REFERENDUM_COMUNI.filter(c => c.voto === 'SI').length
  const totNo = REFERENDUM_COMUNI.filter(c => c.voto === 'NO').length
  const totale = REFERENDUM_COMUNI.length

  const topSi = [...REFERENDUM_COMUNI.filter(c => c.voto === 'SI')]
    .sort((a, b) => b.percentuale - a.percentuale)
    .slice(0, 5)

  const topNo = [...REFERENDUM_COMUNI.filter(c => c.voto === 'NO')]
    .sort((a, b) => b.percentuale - a.percentuale)
    .slice(0, 5)

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px 0' }}>
      {/* Divider + label */}
      <div style={{ marginBottom: 32, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: 'var(--font-mono)' }}>Novità</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>

      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#8b5cf620', borderRadius: 6, padding: '3px 10px', marginBottom: 10 }}>
            <Vote size={12} style={{ color: '#8b5cf6' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Referendum Costituzionale 2026</span>
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 3vw, 30px)', fontWeight: 800, color: 'var(--text-0)', letterSpacing: '-0.02em', lineHeight: 1.2, margin: 0 }}>
            Come ha votato la Brianza?
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 8, lineHeight: 1.6 }}>
            Il 22 e 23 marzo 2026 i comuni della provincia di Monza e della Brianza si sono espressi sul referendum costituzionale.
            Su <strong>{totale} comuni</strong> monitorati, il SÌ ha prevalso in <strong style={{ color: '#10b981' }}>{totSi}</strong> e il NO in <strong style={{ color: '#ef4444' }}>{totNo}</strong>.
          </p>
        </div>
        <Link to="/referendum" style={{ textDecoration: 'none', flexShrink: 0 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 10, fontWeight: 700, fontSize: 13,
            background: '#8b5cf6', color: '#fff', cursor: 'pointer',
            transition: 'opacity 0.15s',
          }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.opacity = '0.85'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.opacity = '1'}
          >
            Vedi tutti i risultati
            <ArrowRight size={14} />
          </div>
        </Link>
      </div>

      {/* Cards SÌ / NO */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {/* Card SÌ */}
        <div style={{
          background: 'var(--bg-1)', border: '1px solid #10b98130',
          borderTop: '3px solid #10b981', borderRadius: 14,
          padding: '20px 20px 16px', display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#10b98118', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle size={16} style={{ color: '#10b981' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--font-mono)' }}>Prevalenza SÌ</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981', fontFamily: 'var(--font-mono)', lineHeight: 1.1 }}>{totSi} comuni</div>
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-3)' }}>{Math.round((totSi / totale) * 100)}%</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--font-mono)', marginBottom: 2 }}>Top comuni per margine</div>
            {topSi.map(c => (
              <div key={c.nome} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 5, background: 'var(--bg-2)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(c.percentuale - 50) * 8}%`, background: '#10b981', borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-2)', width: 140, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', width: 44, textAlign: 'right', flexShrink: 0, fontFamily: 'var(--font-mono)' }}>{c.percentuale.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Card NO */}
        <div style={{
          background: 'var(--bg-1)', border: '1px solid #ef444430',
          borderTop: '3px solid #ef4444', borderRadius: 14,
          padding: '20px 20px 16px', display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ef444418', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XCircle size={16} style={{ color: '#ef4444' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--font-mono)' }}>Prevalenza NO</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#ef4444', fontFamily: 'var(--font-mono)', lineHeight: 1.1 }}>{totNo} comuni</div>
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-3)' }}>{Math.round((totNo / totale) * 100)}%</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--font-mono)', marginBottom: 2 }}>Top comuni per margine</div>
            {topNo.map(c => (
              <div key={c.nome} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 5, background: 'var(--bg-2)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(c.percentuale - 50) * 8}%`, background: '#ef4444', borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-2)', width: 140, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', width: 44, textAlign: 'right', flexShrink: 0, fontFamily: 'var(--font-mono)' }}>{c.percentuale.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ScreenshotsSection() {
  return (
    <div style={{ background: 'var(--bg-1)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '72px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: 'var(--font-mono)', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            <Layers size={12} />
            Funzionalità
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 800, color: 'var(--text-0)', letterSpacing: '-0.02em', lineHeight: 1.15, marginBottom: 14 }}>
            Esplora i dati in ogni sezione
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.75, maxWidth: 520, margin: '0 auto' }}>
            Ogni sezione offre tabelle filtrabili, mappe e KPI aggiornati per ogni ambito della provincia.
          </p>
        </div>

        {/* Screenshot cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
          {SCREENSHOTS.map(s => (
            <div key={s.title} style={{
              borderRadius: 16,
              overflow: 'hidden',
              border: '1px solid var(--border)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
              background: '#1e293b',
            }}>
              {/* Browser chrome */}
              <div style={{ background: '#1e293b', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', opacity: 0.8 }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', opacity: 0.8 }} />
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', opacity: 0.8 }} />
                </div>
                <div style={{ flex: 1, background: '#334155', borderRadius: 5, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, opacity: 0.7 }} />
                  <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>{s.url}</span>
                </div>
              </div>

              {/* Screenshot image with bottom fade */}
              <div style={{ position: 'relative', overflow: 'hidden', lineHeight: 0 }}>
                <img
                  src={s.img}
                  alt={s.title}
                  style={{ width: '100%', display: 'block', maxHeight: 280, objectFit: 'cover', objectPosition: 'top' }}
                />
                {/* Fade at bottom to simulate cut */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(to bottom, transparent, #1e293b)' }} />
              </div>

              {/* Footer label */}
              <div style={{ padding: '10px 16px', background: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: s.color }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0' }}>{s.title}</span>
                <span style={{ fontSize: 10, color: '#64748b', marginLeft: 2 }}>— {s.subtitle}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ── Section cards data ──────────────────────────────────────────────── */

const SECTION_CARDS = [
  {
    path: '/datidemografici',
    label: 'Dati Demografici',
    icon: BarChart2,
    color: '#06b6d4',
    desc: 'Popolazione, densità, indice di vecchiaia e struttura demografica per ambito territoriale.',
    preview: <PreviewDemografici />,
  },
 
  {
    path: '/casedellacomunita',
    label: 'Case della Comunità',
    icon: Heart,
    color: '#e11d48',
    desc: 'Mappatura delle Case della Comunità, servizi erogati e investimenti PNRR.',
    preview: <PreviewCdc />,
  },
  {
    path: '/strutturesanitarie',
    label: 'Strutture Sanitarie',
    icon: Building2,
    color: '#0ea5e9',
    desc: 'Presidi sanitari ASST Brianza: ospedali, ambulatori, consultori, CPS e altro.',
    preview: <PreviewStrutture />,
  },
  {
    path: '/rsa',
    label: 'RSA',
    icon: Bed,
    color: '#6366f1',
    desc: 'Residenze Sanitarie Assistenziali: rette, liste di attesa e nuclei Alzheimer.',
    preview: <PreviewRsa />,
  },
   {
    path: '/pianidizona',
    label: 'Piani di Zona',
    icon: FileText,
    color: '#10b981',
    desc: 'Obiettivi, azioni e aree di intervento dei piani sociali per ambito di zona.',
    preview: <PreviewPdz />,
  },
  {
    path: '/poloterritoriale',
    label: 'Polo Territoriale',
    icon: Layers,
    color: '#8b5cf6',
    desc: 'Servizi sociali e unità operative del polo territoriale per area.',
    preview: null,
  },
  {
    path: '/news',
    label: 'Notizie',
    icon: Newspaper,
    color: '#f59e0b',
    desc: 'Comunicati e aggiornamenti da ASST Brianza e CSV Lombardia.',
    preview: <PreviewNews />,
  },
  
  {
    path: '/cdi',
    label: 'Centri Diurni Integrati',
    icon: Sun,
    color: '#f59e0b',
    desc: 'CDI della provincia: servizi, costi e disponibilità per anziani non autosufficienti.',
    preview: null,
  },
  {
    path: '/rsd',
    label: 'Residenze Disabili',
    icon: Shield,
    color: '#10b981',
    desc: 'RSD: strutture residenziali per persone con disabilità grave.',
    preview: null,
  },
  {
    path: '/css',
    label: 'Comunità Socio Sanitarie',
    icon: Home,
    color: '#06b6d4',
    desc: 'CSS: comunità per persone con disabilità e progettazione del percorso di vita.',
    preview: null,
  },
  {
    path: '/cdd',
    label: 'Centri Diurni Disabili',
    icon: Activity,
    color: '#e11d48',
    desc: 'CDD della provincia: servizi diurni educativi e riabilitativi per persone con disabilità.',
    preview: null,
  },
]

/* ── Main component ──────────────────────────────────────────────────── */

export default function HomePage() {
  const navigate = useNavigate()
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate('/datidemografici', { replace: true })
      } else {
        setChecking(false)
      }
    })
  }, [navigate])

  if (checking) return null

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-0)',
      color: 'var(--text-1)',
      fontFamily: 'var(--font-body)',
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center', overflow: 'hidden' }}>

        {/* Background glow */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse, rgba(6,182,212,0.12) 0%, transparent 70%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: '15%', left: '20%', width: 350, height: 250, background: 'radial-gradient(ellipse, rgba(139,92,246,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', top: '30%', right: '10%', width: 280, height: 200, background: 'radial-gradient(ellipse, rgba(16,185,129,0.07) 0%, transparent 70%)', borderRadius: '50%' }} />
          {/* Dot grid */}
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 720 }}>
          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)', borderRadius: 999, padding: '5px 14px', marginBottom: 32 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#06b6d4', boxShadow: '0 0 8px #06b6d4', animation: 'radar-blink 2s step-end infinite' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#06b6d4', letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}>
              Provincia di Monza e della Brianza
            </span>
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(36px, 6vw, 68px)',
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: '-0.02em',
            color: 'var(--text-0)',
            marginBottom: 24,
          }}>
            Brianza{' '}
            <span style={{ background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Social Debug
            </span>
          </h1>

          {/* Subtitle */}
          <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'var(--text-2)', lineHeight: 1.7, maxWidth: 580, margin: '0 auto 40px', fontWeight: 400 }}>
            Una dashboard open-data per esplorare i servizi socio-sanitari della Provincia di Monza e della Brianza.
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/auth" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
              color: '#fff', borderRadius: 10, padding: '12px 24px',
              fontSize: 14, fontWeight: 700, textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(6,182,212,0.35)',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}>
              Inizia subito
              <ArrowRight size={15} />
            </Link>
            <HeroTicker />
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 32, justifyContent: 'center', marginTop: 56, flexWrap: 'wrap' }}>
            {ROTATING_STATS.map((items, i) => (
              <RotatingStat key={i} items={items} initialDelay={i * 900 + Math.random() * 600} />
            ))}
          </div>
        </div>

        {/* Scroll hint */}
        <div style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, opacity: 0.35 }}>
          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-3)' }}>Esplora</span>
          <div style={{ width: 1, height: 36, background: 'linear-gradient(to bottom, var(--text-3), transparent)' }} />
        </div>
      </div>

      {/* ── Featured sections ──────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px' }}>

        <div style={{ marginBottom: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Dashboard</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 4vw, 38px)', fontWeight: 800, color: 'var(--text-0)', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
            Tutto<br />in un unico posto
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-2)', marginTop: 12, maxWidth: 480, margin: '12px auto 0' }}>
            Dati aggiornati, grafici interattivi e comparazioni tra ambiti per ogni area tematica.
          </p>
        </div>

        {/* Featured 3-card row with previews */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20 }}>
          {SECTION_CARDS.filter(s => s.preview).map(section => {
            const Icon = section.icon
            return (
              <Link key={section.path} to={section.path} style={{ textDecoration: 'none' }}>
                <div style={{
                  background: '#ffffff',
                  border: `1px solid ${section.color}30`,
                  borderTop: `2px solid ${section.color}`,
                  borderRadius: 16,
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer',
                }}
                  onMouseEnter={e => {
                    ;(e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'
                    ;(e.currentTarget as HTMLElement).style.boxShadow = `0 8px 28px rgba(0,0,0,0.1), 0 0 0 1px ${section.color}44`
                  }}
                  onMouseLeave={e => {
                    ;(e.currentTarget as HTMLElement).style.transform = ''
                    ;(e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'
                  }}
                >
                  {/* Card header */}
                  <div style={{ padding: '16px 16px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: `${section.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={16} style={{ color: section.color }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{section.label}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 1, lineHeight: 1.4 }}>{section.desc}</div>
                    </div>
                  </div>

                  {/* Preview area */}
                  <div style={{ borderTop: `1px solid ${section.color}18`, background: `${section.color}06` }}>
                    {section.preview}
                  </div>

                  {/* Footer */}
                  <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${section.color}18` }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: section.color }}>Vai alla sezione</span>
                    <ArrowRight size={13} style={{ color: section.color }} />
                  </div>
                </div>
              </Link>
            )
          })}
        </div>

      </div>

      <RadarSection />

      <ScreenshotsSection />

      {/* ── Referendum spotlight ───────────────────────────────────────── */}
      <ReferendumSpotlight />

      {/* ── All sections grid ──────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px 0' }}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: 'var(--font-mono)' }}>Tutte le sezioni</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {SECTION_CARDS.map(section => {
            const Icon = section.icon
            return (
              <Link key={section.path} to={section.path} style={{ textDecoration: 'none' }}>
                <div style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  padding: '14px 16px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'border-color 0.15s, background 0.15s',
                  cursor: 'pointer',
                }}
                  onMouseEnter={e => {
                    ;(e.currentTarget as HTMLElement).style.borderColor = section.color + '55'
                    ;(e.currentTarget as HTMLElement).style.background = `${section.color}08`
                  }}
                  onMouseLeave={e => {
                    ;(e.currentTarget as HTMLElement).style.borderColor = ''
                    ;(e.currentTarget as HTMLElement).style.background = 'var(--card-bg)'
                  }}
                >
                  <div style={{ width: 30, height: 30, borderRadius: 7, background: `${section.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={14} style={{ color: section.color }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-0)', lineHeight: 1.3 }}>{section.label}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>Apri →</div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>

        {/* ── Footer strip ─────────────────────────────────────────── */}
        <div style={{ marginTop: 80, paddingTop: 32, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--text-0)' }}>Brianza Social Debug</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>Open data — Provincia di Monza e della Brianza</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/auth" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.2)',
              color: '#06b6d4', borderRadius: 8, padding: '8px 16px',
              fontSize: 13, fontWeight: 600, textDecoration: 'none',
            }}>
              <Users size={13} />
              Inizia subito
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
